export const PLAN_PATHS = [
  { id: "general", title: "General Bermuda Guest", cover: "/guides/cover-guest-guide.jpg", copy: "The complete island planner for any stay, any pace." },
  { id: "couples", title: "Couples Escape", cover: "/guides/cover-couples-guide.jpg", copy: "Romance-first recommendations for two." },
  { id: "kids", title: "Bermuda With Kids", cover: "/guides/cover-kids-guide.jpg", copy: "Family picks matched to your children's ages." },
  { id: "luxury", title: "Luxury Bermuda", cover: "/guides/cover-luxury-guide.jpg", copy: "Curated, elevated, unhurried." },
  { id: "cruise", title: "Cruise Ship Visit", cover: "/guides/cover-cruise-guide.jpg", copy: "Make every hour ashore count." },
];

export const INTEREST_OPTIONS = [
  ["beach", "Beaches"], ["adventure", "Adventure"], ["history", "History & Culture"], ["shopping", "Shopping"],
  ["nightlife", "Nightlife"], ["wellness", "Wellness & Spa"], ["boating", "Boating"], ["snorkelling", "Snorkelling & Diving"],
  ["fishing", "Fishing"], ["golf", "Golf"], ["family", "Family Activities"], ["romance", "Romance"],
  ["photography", "Photography"], ["local_food", "Local Food"],
];

export const CORE_QUESTIONS = [
  { id: "arrival", label: "Arrival date", type: "date" },
  { id: "departure", label: "Departure date", type: "date" },
  { id: "staying", label: "Where are you staying?", type: "text", placeholder: "Hotel, rental or area" },
  { id: "party", label: "Who's travelling?", type: "party" },
  { id: "firstTrip", label: "First trip to Bermuda?", type: "choice", options: [["yes", "First time"], ["no", "Been before"]] },
  { id: "budget", label: "Budget style", type: "choice", options: [["1", "$ — Easy"], ["2", "$$ — Mid-range"], ["3", "$$$ — Treat us"], ["4", "Luxury"]] },
  { id: "transport", label: "Getting around", type: "multi", options: [["bus", "Bus & ferry"], ["taxi", "Taxi / rides"], ["microcar", "Electric microcar"], ["walk", "Mostly walking"]] },
  { id: "interests", label: "What do you love?", type: "multi", options: INTEREST_OPTIONS },
  { id: "avoid", label: "Anything you'd rather skip?", type: "multi", options: INTEREST_OPTIONS },
  { id: "mustDo", label: "Things I definitely want to do", type: "text", placeholder: "Optional" },
  { id: "food", label: "Food preferences / allergies", type: "text", placeholder: "Optional" },
  { id: "mobility", label: "Mobility or accessibility needs", type: "text", placeholder: "Optional" },
];

export const PATH_QUESTIONS = {
  couples: [
    { id: "occasion", label: "What kind of escape is this?", type: "choice", options: [["getaway", "Romantic getaway"], ["honeymoon", "Honeymoon"], ["anniversary", "Anniversary"], ["birthday", "Birthday"], ["proposal", "Proposal"], ["babymoon", "Babymoon"], ["just-because", "Just because"]] },
  ],
  kids: [
    { id: "kidAges", label: "Ages of the children", type: "text", placeholder: "e.g. 2, 7, 12" },
  ],
  luxury: [
    { id: "occasion", label: "Celebrating anything?", type: "text", placeholder: "Optional" },
  ],
  cruise: [
    { id: "ship", label: "Ship name", type: "text", placeholder: "e.g. Celebrity Eclipse" },
    { id: "dock", label: "Which dock?", type: "choice", options: [["dockyard", "Royal Naval Dockyard"], ["hamilton", "Hamilton"], ["stgeorges", "St. George's"]] },
    { id: "ashoreStart", label: "Time you get ashore", type: "time" },
    { id: "allAboard", label: "All-aboard time", type: "time" },
  ],
  general: [],
};

export const CONCIERGE_SERVICES = [
  "Restaurant reservations", "Transportation", "Boat charter", "Private chef", "Birthday setup",
  "Anniversary surprise", "Grocery pre-stock", "Beach setup", "Babysitting", "Cleaning during stay", "Other",
];

const parseAges = (s) => (String(s || "").match(/\d+/g) || []).map(Number).filter((n) => n < 18);

export function hoursAshore(a) {
  if (!a.ashoreStart || !a.allAboard) return null;
  const [h1, m1] = a.ashoreStart.split(":").map(Number);
  const [h2, m2] = a.allAboard.split(":").map(Number);
  const hours = (h2 * 60 + m2 - h1 * 60 - m1) / 60;
  return hours > 0 ? hours : null;
}

export function tripDays(a) {
  if (!a.arrival || !a.departure) return 3;
  const d = Math.round((new Date(a.departure) - new Date(a.arrival)) / 86400000);
  return Math.min(Math.max(d, 1), 14);
}

export function scoreRecommendations(recs, a, path) {
  const interests = a.interests || [];
  const avoid = a.avoid || [];
  const budget = Number(a.budget || 2);
  const ages = parseAges(a.kidAges);
  const hours = path === "cruise" ? hoursAshore(a) : null;
  return recs
    .map((r) => {
      if (!(r.audiences || []).includes(path) && !(r.audiences || []).includes("general")) return null;
      if (avoid.some((i) => (r.interests || []).includes(i))) return null;
      if (hours && r.duration_hours > Math.max(hours - 1, 1)) return null;
      let s = 0;
      if ((r.audiences || []).includes(path)) s += 1;
      s += interests.filter((i) => (r.interests || []).includes(i)).length * 2;
      if (path === "luxury") {
        if (r.price_level >= 3) s += 2;
      } else {
        if (r.price_level <= budget) s += 2;
        else if (r.price_level > budget + 1) s -= 2;
      }
      if (path === "kids" && ages.length) {
        const need = Math.min(...ages) <= 4 ? "toddler" : Math.min(...ages) <= 12 ? "kid" : "teen";
        s += (r.ages || []).includes(need) ? 3 : -3;
      }
      if (path === "cruise" && r.parish === "Sandys") s += 3;
      if (path === "couples" && (r.interests || []).includes("romance")) s += 2;
      return { ...r, _score: s };
    })
    .filter(Boolean)
    .sort((x, y) => y._score - x._score);
}

export function buildItinerary(savedRecs, days, cruise) {
  const count = Math.max(days, 1);
  const plan = Array.from({ length: count }, (_, i) => ({ day: i + 1, slots: [] }));
  if (cruise?.ashoreStart) {
    plan[0].slots.push({ time: cruise.ashoreStart, label: "Arrive ashore", custom: true });
  }
  const dinings = savedRecs.filter((r) => r.category === "dining");
  const others = savedRecs.filter((r) => r.category !== "dining");
  others.forEach((r, i) => {
    const day = plan[i % count];
    const morningCount = day.slots.filter((s) => s.time === "Morning").length;
    const afternoonCount = day.slots.filter((s) => s.time === "Afternoon").length;
    const time = morningCount <= afternoonCount ? "Morning" : "Afternoon";
    day.slots.push({ time, label: r.name, recId: r.id, duration: r.duration_hours });
  });
  dinings.forEach((r, i) => {
    const day = plan[i % count];
    const hasDinner = day.slots.some((s) => s.time === "Evening");
    day.slots.push({ time: hasDinner ? "Lunch" : "Evening", label: r.name, recId: r.id, duration: r.duration_hours });
  });
  if (cruise?.allAboard) {
    plan[count - 1].slots.push({ time: cruise.allAboard, label: "Return to ship — all aboard", custom: true, cruiseReturn: true });
  }
  const order = ["Morning", "Lunch", "Afternoon", "Evening"];
  plan.forEach((d) => d.slots.sort((a, b) => {
    const at = a.custom ? a.time : String(order.indexOf(a.time)).padStart(2, "0");
    const bt = b.custom ? b.time : String(order.indexOf(b.time)).padStart(2, "0");
    return at.localeCompare(bt);
  }));
  return plan;
}

export const priceLabel = (n) => ["$", "$$", "$$$", "$$$$"][Math.min(Math.max((n || 1) - 1, 0), 3)];

export const calendarLink = (rec, a, type) => {
  const u = new URLSearchParams();
  u.set("type", type);
  u.set("title", rec.name);
  if (a?.arrival) u.set("date", a.arrival);
  if (rec.parish) u.set("location", `${rec.parish}, Bermuda`);
  return `/calendar?${u.toString()}`;
};
